import React, { useState, useEffect } from 'react';
import logoYellow from '../../assets/logo/Qr-table - yellow.svg';

const PageLoader = ({ theme = 'auto', logoUrl = null, fullScreen = true, minHeight = '320px' }) => {
    const [isDark, setIsDark] = useState(() => {
        if (theme === 'dark') return true;
        if (theme === 'light') return false;
        const docTheme = typeof document !== 'undefined' ? (document.documentElement.getAttribute('data-theme') || document.body.getAttribute('data-theme')) : null;
        if (docTheme === 'dark') return true;
        if (docTheme === 'light') return false;
        return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    useEffect(() => {
        if (theme === 'dark') {
            setIsDark(true);
            return;
        }
        if (theme === 'light') {
            setIsDark(false);
            return;
        }

        const checkTheme = () => {
            const docTheme = document.documentElement.getAttribute('data-theme') || document.body.getAttribute('data-theme');
            if (docTheme === 'dark') {
                setIsDark(true);
            } else if (docTheme === 'light') {
                setIsDark(false);
            } else if (window.matchMedia) {
                setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
            }
        };

        checkTheme();

        const observer = new MutationObserver(checkTheme);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

        const mediaQuery = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            const docTheme = document.documentElement.getAttribute('data-theme');
            if (!docTheme) setIsDark(e.matches);
        };

        if (mediaQuery) {
            if (mediaQuery.addEventListener) {
                mediaQuery.addEventListener('change', handleChange);
            } else if (mediaQuery.addListener) {
                mediaQuery.addListener(handleChange);
            }
        }

        return () => {
            observer.disconnect();
            if (mediaQuery) {
                if (mediaQuery.removeEventListener) {
                    mediaQuery.removeEventListener('change', handleChange);
                } else if (mediaQuery.removeListener) {
                    mediaQuery.removeListener(handleChange);
                }
            }
        };
    }, [theme]);

    const bgColor = fullScreen ? (isDark ? '#12141A' : '#FFFFFF') : 'transparent';
    const barBg = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)';
    const displayLogo = logoUrl || logoYellow;

    const containerStyle = fullScreen ? {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: bgColor,
        transition: 'background-color 0.3s ease',
        fontFamily: "'Comfortaa', sans-serif",
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        zIndex: 99999
    } : {
        width: '100%',
        minHeight: minHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: bgColor,
        padding: '0',
        fontFamily: "'Comfortaa', sans-serif",
        position: 'relative'
    };

    return (
        <div style={containerStyle}>
            {/* Compact Scanner Frame */}
            <div className="scanner-container" style={{
                position: 'relative',
                width: '74px',
                height: '74px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px'
            }}>
                {/* Corner Scanner Reticles */}
                <span style={{ position: 'absolute', top: 0, left: 0, width: '12px', height: '12px', borderTop: '2.5px solid #FFE000', borderLeft: '2.5px solid #FFE000', borderRadius: '4px 0 0 0' }}></span>
                <span style={{ position: 'absolute', top: 0, right: 0, width: '12px', height: '12px', borderTop: '2.5px solid #FFE000', borderRight: '2.5px solid #FFE000', borderRadius: '0 4px 0 0' }}></span>
                <span style={{ position: 'absolute', bottom: 0, left: 0, width: '12px', height: '12px', borderBottom: '2.5px solid #FFE000', borderLeft: '2.5px solid #FFE000', borderRadius: '0 0 0 4px' }}></span>
                <span style={{ position: 'absolute', bottom: 0, right: 0, width: '12px', height: '12px', borderBottom: '2.5px solid #FFE000', borderRight: '2.5px solid #FFE000', borderRadius: '0 0 4px 0' }}></span>

                {/* Logo & Clipped Laser Scan Area */}
                <div style={{
                    width: '58px',
                    height: '58px',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <img
                        src={displayLogo}
                        alt="Logo"
                        onError={(e) => { e.target.src = logoYellow; }}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: logoUrl ? 'cover' : 'contain',
                            borderRadius: '18px',
                            animation: 'logo-pulse 2s infinite ease-in-out'
                        }}
                    />

                    {/* Scanning Laser Beam */}
                    <div style={{
                        position: 'absolute',
                        left: '-10%',
                        width: '120%',
                        height: '2.5px',
                        background: 'linear-gradient(90deg, transparent, #FFE000 50%, #ffffff 75%, transparent)',
                        boxShadow: '0 0 10px #FFE000, 0 0 20px #FFE000',
                        borderRadius: '2px',
                        animation: 'scan-laser 1.6s infinite ease-in-out'
                    }}></div>
                </div>
            </div>

            {/* Compact Progress Bar */}
            <div style={{
                width: '80px',
                height: '3px',
                background: barBg,
                borderRadius: '3px',
                overflow: 'hidden',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    height: '100%',
                    width: '45%',
                    background: 'linear-gradient(90deg, #FFE000, #FF9B1A)',
                    borderRadius: '3px',
                    boxShadow: '0 0 8px rgba(255, 224, 0, 0.6)',
                    animation: 'loading-slide 1.4s infinite ease-in-out'
                }}></div>
            </div>

            <style>{`
                @keyframes scan-laser {
                    0% { top: -10%; opacity: 0; }
                    15% { opacity: 1; }
                    85% { opacity: 1; }
                    100% { top: 110%; opacity: 0; }
                }

                @keyframes logo-pulse {
                    0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0px rgba(255,224,0,0)); }
                    50% { transform: scale(1.03); filter: drop-shadow(0 0 8px rgba(255,224,0,0.3)); }
                }

                @keyframes loading-slide {
                    0% { left: -45%; }
                    100% { left: 100%; }
                }
            `}</style>
        </div>
    );
};

export default PageLoader;
